import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  signInWithCredential,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  OAuthProvider,
} from 'firebase/auth';
import { firebaseAuth } from './firebaseconfig';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Dialog } from '@capacitor/dialog';
import { sha256 } from 'js-sha256';
import {
  SignInWithApple,
  SignInWithAppleResponse,
  SignInWithAppleOptions,
} from '@capacitor-community/apple-sign-in';
import { Capacitor } from '@capacitor/core';

function generateNonce(length = 32) {
  const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  let result = '';
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  bytes.forEach((b) => (result += charset[b % charset.length]));
  return result;
}

export const signInWithGoogle = async () => {
  const result = await FirebaseAuthentication.signInWithGoogle();
  const credential = GoogleAuthProvider.credential(result.credential?.idToken);
  const auth = await firebaseAuth();
  await signInWithCredential(auth, credential);
};

export const signInWithApple = async () => {

  const rawNonce = generateNonce();
  const hashedNonce = sha256(rawNonce);

  let options: SignInWithAppleOptions = {
    clientId: Capacitor.getPlatform() === 'ios' ? 'online.ilmo' : 'online.ilmo.www',
    redirectURI: 'https://ilmo.online/__/auth/handler',
    scopes: 'email',
    nonce: hashedNonce,
  };

  if (Capacitor.getPlatform() === 'ios') {
    await SignInWithApple.authorize(options)
      .then(async (result: SignInWithAppleResponse) => {
        const provider = new OAuthProvider('apple.com');
        const credential = provider.credential({
          idToken: result.response.identityToken || '',
          rawNonce: rawNonce,
        });
        const auth = await firebaseAuth();
        await signInWithCredential(auth, credential);
        return {
          success: true,
          user: auth.currentUser,
          error: null,
        };
      })
      .catch(error => {
        return {
          success: false,
          error: error.message,
        };
      });
  } else {
    try {
      const result = await FirebaseAuthentication.signInWithApple();
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({
        idToken: result.credential?.idToken || ''
      });
      const auth = await firebaseAuth();
      await signInWithCredential(auth, credential);
      return {
        success: true,
        user: auth.currentUser,
        error: null,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message,
      };
    }
  }
};

export async function signInWithCredentials(email: string, password: string) {
  const auth = await firebaseAuth();
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return {
    success: true,
    user: userCredential.user,
    error: null,
  };
}

export async function handlePasswordReset(email: string) {
  const auth = await firebaseAuth();

  try {
    await sendPasswordResetEmail(auth, email);

    return {
      success: true,
    };

  } catch (error: any) {
    console.error('Error sending password reset email:', error);

    let errorMessage = 'Failed to send password reset email. Please try again.';

    return {
      success: false,
      error: errorMessage,
    };
  }
}

export async function handlePasswordResetLoggedIn(password: string, currentPassword: string) {
  const auth = await firebaseAuth();

  try {
    if (auth.currentUser) {
      // Re-authenticate the user first
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email!,
        currentPassword
      );
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Now update the password
      await updatePassword(auth.currentUser, password);
    }

    return {
      success: true,
    };

  } catch (error: any) {
    console.error('Error updating password:', error);

    let errorMessage = 'Salasanan vaihtaminen epäonnistui. Kokeile uudelleen.';

    if (error.code === 'auth/requires-recent-login') {
      errorMessage = 'Kirjaudu sisään uudelleen ennen salasanan vaihtamista.';
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
}

export async function validateCurrentPassword(currentPassword: string) {
  const auth = await firebaseAuth();

  try {
    if (!auth.currentUser?.email) {
      return {
        success: false,
        error: 'Käyttäjää ei löydy',
      };
    }

    const credential = EmailAuthProvider.credential(
      auth.currentUser.email,
      currentPassword
    );

    await reauthenticateWithCredential(auth.currentUser, credential);

    return {
      success: true,
      error: null,
    };
  } catch (error: any) {
    console.error('Error validating password:', error);

    let errorMessage = 'Salasanan tarkistus epäonnistui';
    if (error.code === 'auth/wrong-password') {
      errorMessage = 'Virheellinen salasana';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Liian monta yritystä. Yritä myöhemmin uudelleen';
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
}

// Sign out functionality
export const firebaseSignOut = async () => {
  try {
    const auth = await firebaseAuth();
    await signOut(auth);
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message,
    };
  }
};

// Auth state observer
export const onAuthStateChanged = async (callback: (user: any) => void) => {
  const auth = await firebaseAuth();
  return auth.onAuthStateChanged(callback);
};